// src/game/rules.ts
// The forward engine: state, slide, exit, win, hashing + generator-facing primitives.
// Pure & headless (§10 phase 1): no DOM/RNG/time/storage. No function mutates inputs.

import type { Board, Gate } from './board';
import type { Block } from './block';
import type { BlockId, Cell, Direction, Move } from './types';
import { DELTAS, DIRECTIONS, SIDE_EXIT_DIRECTION } from './types';
import { gateCoversLanes, isGateCrossing, isInside } from './board';
import { blockLanes, blockOccupies, createBlock, leadingCells, translateBlock } from './block';

// ---- 6.1 State & result types ----------------------------------------------

/**
 * Full dynamic game state. JSON-serializable. `board` is shared by reference across snapshots
 * (immutable). `blocks` is replaced wholesale on every transition; rules never mutate an existing
 * array/Block. INVARIANTS: no two blocks overlap; every block cell is isInside(board); ids unique
 * & stable; solved ⇔ blocks.length === 0.
 */
export interface GameState {
  readonly board: Board;
  readonly blocks: readonly Block[];
}

/**
 * Result of resolving one slide (§2.3). Discriminated by `kind`:
 *  - 'noop'   : block neither moves nor exits. MUST NOT be recorded in history.
 *  - 'slid'   : block stops on-board after sliding `distance` (>= 1) cells. `block` is the
 *               relocated block (new anchor).
 *  - 'exited' : block reaches its matching gate and is removed. `distance` (>= 0) is the on-board
 *               anchor displacement before the off-board crossing begins. `gate` is the gate it
 *               left through.
 */
export type SlideResult =
  | { readonly kind: 'noop' }
  | { readonly kind: 'slid'; readonly distance: number; readonly block: Block }
  | { readonly kind: 'exited'; readonly distance: number; readonly gate: Gate };

/** Outcome discriminant alias: 'noop' | 'slid' | 'exited'. */
export type MoveOutcome = SlideResult['kind'];

/**
 * The result of APPLYING a move: the resulting state plus the detail.
 *  - kind 'noop'   -> `state` is the SAME reference as the input (===).
 *  - kind 'slid'   -> `state` is a NEW state with that block relocated.
 *  - kind 'exited' -> `state` is a NEW state with that block removed.
 * `blockId` echoes the moved block id (convenience for solver/history).
 */
export interface MoveResult {
  readonly state: GameState;
  readonly result: SlideResult;
  readonly blockId: BlockId;
}

// ---- 6.2 Lookups -----------------------------------------------------------

/** Block with `id`, or undefined. */
export function getBlock(state: GameState, id: BlockId): Block | undefined {
  return state.blocks.find((b) => b.id === id);
}

/** Block occupying `cell`, or undefined if empty/off-board. */
export function blockAt(state: GameState, cell: Cell): Block | undefined {
  if (!isInside(state.board, cell)) {
    return undefined;
  }
  for (const b of state.blocks) {
    if (blockOccupies(b, cell)) {
      return b;
    }
  }
  return undefined;
}

/** True iff all `cells` are free of blocks other than `selfId` (used during a self-slide). */
function frontIsClear(state: GameState, selfId: BlockId, cells: readonly Cell[]): boolean {
  for (const c of cells) {
    const occ = blockAt(state, c);
    if (occ !== undefined && occ.id !== selfId) {
      return false;
    }
  }
  return true;
}

// ---- 6.4 Exit eligibility --------------------------------------------------

/**
 * The unique matching exit gate for (block, dir), or undefined. Lane-coverage rule (§6.4 for
 * polyominoes):
 *   1. dir === SIDE_EXIT_DIRECTION[gate.side]  (the gate faces the way we're moving)
 *   2. block.color === gate.color
 *   3. ONE gate covers ALL of the block's perpendicular lanes: gateCoversLanes(gate,
 *      blockLanes(block, dir)). A wide L therefore exits only through a gate whose span covers
 *      every column it occupies; a span-1 gate can still pass a 1×N piece moving along its long
 *      axis (cross-section 1 → a single lane).
 * This is purely a (color + side + coverage) predicate; whether the block can actually REACH the
 * gate (clear path, valid off-board crossing cells) is decided per-step inside resolveSlide.
 */
export function matchingExitGate(board: Board, block: Block, dir: Direction): Gate | undefined {
  const lanes = blockLanes(block, dir);
  for (const gate of board.gates) {
    if (SIDE_EXIT_DIRECTION[gate.side] !== dir) {
      continue;
    }
    if (gate.color !== block.color) {
      continue;
    }
    if (!gateCoversLanes(gate, lanes)) {
      continue;
    }
    return gate;
  }
  return undefined;
}

/**
 * True iff `block` could exit through SOME gate by moving `dir`, satisfying §6.4 (color + alignment).
 * Hay cover is COSMETIC (§4.1: "does not change reachability") — a covered block still exits its
 * matching gate; sliding merely peels a hay layer (see obstacles.revealOnSlide). This is exactly what
 * keeps hay safe to drop onto generated levels without re-verifying solvability.
 */
export function isExitEligible(board: Board, block: Block, dir: Direction): boolean {
  return matchingExitGate(board, block, dir) !== undefined;
}

// ---- 6.5 Slide algorithm ---------------------------------------------------

/**
 * EXIT_DISTANCE for a board: rows + cols. Strictly larger than the longest on-board displacement
 * any single piece can travel, so a slide requested at this distance is GUARANTEED to reach the
 * far wall — or, when a matching gate is in the way, to take the off-board exit step. `legalMoves`
 * encodes every exit move with this distance; `applyMove`/`resolveSlide` then re-derive the exit
 * naturally (the slide loop clamps/exits on its own). It is board-dependent, hence a function
 * rather than a single module constant.
 */
export function exitDistance(board: Board): number {
  return board.rows + board.cols;
}

/**
 * Pure, bounded free-move slide resolution (§2.3). Deterministic in (state, id, dir, distance).
 * Throws if `id` is absent (caller precondition). Does NOT mutate state. Omitting `distance`
 * defaults to EXIT_DISTANCE (slide as far as possible — to the wall or out a matching gate).
 *
 * Steps the block one cell at a time, up to `distance` cells:
 *  - At step t (0-based) the would-be position is translateBlock(block,dir,t) and its frontier is
 *    leadingCells(pos,dir) — the cells one step ahead of the leading silhouette edge.
 *  - If every leading cell is on-board AND clear of OTHER blocks, the step is allowed; keep going
 *    while t < distance (this is the cap: a slide NEVER passes through a wall or another piece).
 *  - If a leading cell crosses the perimeter, attempt EXIT: a single matching gate (color + side +
 *    lane coverage) must exist, every OFF-board leading cell must be a valid crossing of it, and
 *    any remaining ON-board leading cells must be clear (never trail the body through a piece). On
 *    success → { kind:'exited', distance:t }. Otherwise the perimeter is a wall → stop.
 *  - If a leading cell hits another block → stop.
 *  - Stopping with t>=1 → 'slid' (relocated block); with t===0 → 'noop'.
 *
 * A 1×N block exits only along its long axis (cross-section 1 = a single lane), so a span-1 gate
 * passes it; a perpendicular approach spans >1 lanes and no span-1 gate covers them → it rests.
 */
export function resolveSlide(
  state: GameState,
  id: BlockId,
  dir: Direction,
  distance: number = exitDistance(state.board),
): SlideResult {
  const block = getBlock(state, id);
  if (block === undefined) {
    throw new Error(`resolveSlide: no block with id ${id}`);
  }
  const board = state.board;
  let t = 0; // accepted on-board anchor displacement
  for (;;) {
    if (t >= distance) {
      break; // reached the requested distance cap -> rest here
    }
    const pos = translateBlock(block, dir, t);
    const front = leadingCells(pos, dir);
    const offBoard = front.filter((c) => !isInside(board, c));
    if (offBoard.length === 0) {
      // Step stays on-board.
      if (frontIsClear(state, id, front)) {
        t += 1;
        continue;
      }
      break; // hit another block -> STOP at t
    }
    // Leading edge crosses the perimeter: exit iff ONE matching gate covers all lanes, every
    // off-board leading cell is a valid crossing of it, and any on-board leading cells are clear.
    const gate = matchingExitGate(board, block, dir);
    if (
      gate !== undefined &&
      offBoard.every((c) => isGateCrossing(board, gate, dir, c)) &&
      frontIsClear(state, id, front.filter((c) => isInside(board, c)))
    ) {
      return { kind: 'exited', distance: t, gate };
    }
    break; // wall OR non-matching/uncovered gate = solid
  }
  if (t === 0) {
    return { kind: 'noop' };
  }
  return { kind: 'slid', distance: t, block: translateBlock(block, dir, t) };
}

// ---- 6.3 Slide application -------------------------------------------------

/** Apply a slide; return new state (or same ref on 'noop') + result. Never mutates input. */
export function applySlide(
  state: GameState,
  id: BlockId,
  dir: Direction,
  distance: number = exitDistance(state.board),
): MoveResult {
  const result = resolveSlide(state, id, dir, distance);
  switch (result.kind) {
    case 'noop':
      return { state, result, blockId: id };
    case 'slid': {
      const relocated = result.block;
      const blocks = state.blocks.map((b) => (b.id === id ? relocated : b));
      const next: GameState = { board: state.board, blocks };
      return { state: next, result, blockId: id };
    }
    case 'exited': {
      const blocks = state.blocks.filter((b) => b.id !== id);
      const next: GameState = { board: state.board, blocks };
      return { state: next, result, blockId: id };
    }
    default: {
      const _exhaustive: never = result;
      return _exhaustive;
    }
  }
}

/** Convenience: applyMove(s, m) === applySlide(s, m.blockId, m.dir, m.distance). */
export function applyMove(state: GameState, move: Move): MoveResult {
  return applySlide(state, move.blockId, move.dir, move.distance);
}

/** Win check (§2.4): solved iff no blocks remain. */
export function isSolved(state: GameState): boolean {
  return state.blocks.length === 0;
}

/**
 * No-op helper (§2.3): true iff the block cannot even move one cell in `dir`. A noop is decided by
 * the very first step, so testing distance 1 is sufficient (and cheaper than an unbounded slide).
 */
export function isNoop(state: GameState, id: BlockId, dir: Direction): boolean {
  return resolveSlide(state, id, dir, 1).kind === 'noop';
}

// ---- 6.7 Generator/solver-facing primitives --------------------------------

/**
 * All legal NON-NOOP moves from `state`, in canonical order: blockId asc, then dir in DIRECTIONS
 * order (up, down, left, right), then by distance ascending with the EXIT move LAST per (block,dir).
 * Empty ⇔ terminal/blocked. The order is total and stable, which is what makes BFS tie-breaking
 * (and therefore the solver) deterministic (S2).
 *
 * Free-move encoding: a block that can slide up to `f = freeSlideDistance` cells in `dir` yields one
 * distinct move per resting distance 1..f (each a real 'slid'). If the unbounded slide additionally
 * EXITS through a matching gate, exactly one more move is appended, encoded with distance =
 * EXIT_DISTANCE (= rows + cols). That sentinel distance is strictly larger than any bounded resting
 * distance (no collision) and guaranteed to carry the piece off-board, so applyMove re-derives an
 * 'exited' result. We only append the exit when the UNBOUNDED slide actually exits (gate reachable
 * with a clear path) — a matching-but-unreachable gate yields a 'slid', and no spurious exit move.
 */
export function legalMoves(state: GameState): readonly Move[] {
  const ids = state.blocks.map((b) => b.id).sort((a, b) => a - b);
  const board = state.board;
  const exitDist = exitDistance(board);
  const moves: Move[] = [];
  for (const id of ids) {
    for (const dir of DIRECTIONS) {
      const full = resolveSlide(state, id, dir, exitDist);
      if (full.kind === 'noop') {
        continue;
      }
      // Bounded resting distances 1..displacement are each a distinct 'slid' move.
      for (let d = 1; d <= full.distance; d++) {
        moves.push({ blockId: id, dir, distance: d });
      }
      if (full.kind === 'exited') {
        moves.push({ blockId: id, dir, distance: exitDist });
      }
    }
  }
  return moves;
}

/**
 * True iff a forward slide in `dir` would NOT move the block one cell: block is flush/resting
 * against a wall, another block, or a non-matching gate in `dir`. Equivalent to isNoop; kept as a
 * named generator-facing primitive (resolveSlide(...,1).kind === 'noop').
 */
export function isResting(state: GameState, id: BlockId, dir: Direction): boolean {
  return resolveSlide(state, id, dir, 1).kind === 'noop';
}

/**
 * Empty cells the block can be slid in `dir` before hitting anything (>= 0). 'slid' -> distance;
 * 'exited' -> cells to the gate; 'noop' -> 0. Bounds reverse-slide k.
 */
export function freeSlideDistance(state: GameState, id: BlockId, dir: Direction): number {
  const r = resolveSlide(state, id, dir);
  switch (r.kind) {
    case 'noop':
      return 0;
    case 'slid':
      return r.distance;
    case 'exited':
      return r.distance;
    default: {
      const _exhaustive: never = r;
      return _exhaustive;
    }
  }
}

/** Normalized cover for hashing/equality: absent ⇒ 0 (cover present always >= 1 by invariant). */
function coverKey(block: Block): number {
  return block.cover ?? 0;
}

/**
 * Canonical, collision-free string key of the DYNAMIC state for the solver visited-set (§4.1).
 * Encodes blocks sorted by id -> color + shape + anchor + cover. Board geometry is constant
 * within a solve and is omitted. Guarantee: stateEquals(a,b) ⟺ hashState(a) === hashState(b).
 * Fields are colon-delimited; shape cells use row.col notation (integers), ';'-delimited, sorted
 * by normalizeShape (row-major) so the key is stable and unambiguous across runs/platforms.
 */
export function hashState(state: GameState): string {
  return [...state.blocks]
    .sort((a, b) => a.id - b.id)
    .map(
      (b) =>
        `${b.id}:${b.color}:${b.shape.map((o) => `${o.row}.${o.col}`).join(';')}:${b.anchor.row},${b.anchor.col}:${coverKey(b)}`,
    )
    .join('|');
}

/**
 * Structural equality of dynamic state (blocks-by-id: color, shape, anchor, cover).
 * hashState's correctness oracle; used by duality tests & verify.
 */
export function stateEquals(a: GameState, b: GameState): boolean {
  if (a.blocks.length !== b.blocks.length) {
    return false;
  }
  const byId = new Map<BlockId, Block>();
  for (const blk of a.blocks) {
    byId.set(blk.id, blk);
  }
  for (const blk of b.blocks) {
    const other = byId.get(blk.id);
    if (other === undefined) {
      return false;
    }
    if (
      other.color !== blk.color ||
      other.anchor.row !== blk.anchor.row ||
      other.anchor.col !== blk.anchor.col ||
      coverKey(other) !== coverKey(blk) ||
      !shapesEqual(other.shape, blk.shape)
    ) {
      return false;
    }
  }
  // Equal lengths + every b-id found in a with matching props ⇒ a bijection (ids are unique).
  return true;
}

/** Cell-by-cell comparison of two normalized shapes. */
function shapesEqual(a: readonly Cell[], b: readonly Cell[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.row !== b[i]!.row || a[i]!.col !== b[i]!.col) return false;
  }
  return true;
}

// ---- Free-roam movement (player drag): reach any position via a clear path -----------------
//
// The player drags a piece to ANY position it can reach by rigid-body unit slides through empty
// cells — turning corners, threading gaps — blocked only where another piece or a wall genuinely
// sits in the path. Distinct from the solver/generator's straight-line slides (which still drive
// generation, par, and hints). Pure & headless like the rest of this module.

/** True iff every cell of `block` placed at `anchor` is on-board and free of OTHER blocks. */
function placeableAt(state: GameState, block: Block, anchor: Cell): boolean {
  for (const o of block.shape) {
    const cell: Cell = { row: anchor.row + o.row, col: anchor.col + o.col };
    if (!isInside(state.board, cell)) {
      return false;
    }
    const occ = blockAt(state, cell);
    if (occ !== undefined && occ.id !== block.id) {
      return false;
    }
  }
  return true;
}

/** New state with `id`'s anchor set to `anchor` (shape/cover preserved; other blocks unchanged). */
function withAnchor(state: GameState, id: BlockId, anchor: Cell): GameState {
  const blocks = state.blocks.map((b) => (b.id === id ? createBlock({ ...b, anchor }) : b));
  return { board: state.board, blocks };
}

/** All anchors reachable by `id`, plus an exit-trigger anchor if the piece can leave its gate. */
export interface ReachResult {
  /** Every reachable resting anchor (includes the current one). */
  readonly anchors: readonly Cell[];
  /** A reachable anchor from which a slide into the matching gate EXITS the piece, else null. */
  readonly exitAnchor: Cell | null;
  /** Direction the piece travels to leave its matching gate (null if it has no gate). */
  readonly exitDir: Direction | null;
}

/**
 * BFS over anchor positions: from the current anchor, step one cell in each cardinal direction into
 * any all-empty placement, until no new positions appear. While exploring, the first reachable
 * anchor from which the piece can slide off through its matching-color gate is recorded as
 * `exitAnchor`. Deterministic; never mutates `state`.
 */
export function reachableAnchors(state: GameState, id: BlockId): ReachResult {
  const block = getBlock(state, id);
  if (block === undefined) {
    throw new Error(`reachableAnchors: no block with id ${id}`);
  }
  const gate = state.board.gates.find((g) => g.color === block.color);
  const exitDir: Direction | null = gate === undefined ? null : SIDE_EXIT_DIRECTION[gate.side];
  const D = exitDistance(state.board);

  const key = (a: Cell): string => `${a.row},${a.col}`;
  const visited = new Set<string>([key(block.anchor)]);
  const anchors: Cell[] = [{ row: block.anchor.row, col: block.anchor.col }];
  const queue: Cell[] = [block.anchor];
  let exitAnchor: Cell | null = null;

  while (queue.length > 0) {
    const a = queue.shift()!; // guarded by length
    if (exitAnchor === null && exitDir !== null) {
      const at = withAnchor(state, id, a);
      if (resolveSlide(at, id, exitDir, D).kind === 'exited') {
        exitAnchor = { row: a.row, col: a.col };
      }
    }
    for (const dir of DIRECTIONS) {
      const d = DELTAS[dir];
      const na: Cell = { row: a.row + d.drow, col: a.col + d.dcol };
      const k = key(na);
      if (visited.has(k)) {
        continue;
      }
      if (placeableAt(state, block, na)) {
        visited.add(k);
        anchors.push(na);
        queue.push(na);
      }
    }
  }
  return { anchors, exitAnchor, exitDir };
}

/**
 * Relocate `id` to `anchor` (free-roam drop). Returns a NEW state with the piece moved, or the SAME
 * ref + 'noop' if the target equals the current anchor or is not a clear placement. Caller should
 * pass an anchor from `reachableAnchors`. Reported as 'slid' with Manhattan distance for callers.
 */
export function applyRelocate(state: GameState, id: BlockId, anchor: Cell): MoveResult {
  const block = getBlock(state, id);
  if (block === undefined) {
    throw new Error(`applyRelocate: no block with id ${id}`);
  }
  if (
    (block.anchor.row === anchor.row && block.anchor.col === anchor.col) ||
    !placeableAt(state, block, anchor)
  ) {
    return { state, result: { kind: 'noop' }, blockId: id };
  }
  const next = withAnchor(state, id, anchor);
  const distance =
    Math.abs(anchor.row - block.anchor.row) + Math.abs(anchor.col - block.anchor.col);
  return { state: next, result: { kind: 'slid', distance, block: getBlock(next, id)! }, blockId: id };
}

/**
 * Exit `id` through its matching gate from a reachable `exitAnchor`: relocate there, then slide off.
 * Returns the new state with the piece removed (or 'noop' if it has no gate / the anchor doesn't exit).
 */
export function applyExitAt(state: GameState, id: BlockId, exitAnchor: Cell): MoveResult {
  const block = getBlock(state, id);
  if (block === undefined) {
    throw new Error(`applyExitAt: no block with id ${id}`);
  }
  const gate = state.board.gates.find((g) => g.color === block.color);
  if (gate === undefined || !placeableAt(state, block, exitAnchor)) {
    return { state, result: { kind: 'noop' }, blockId: id };
  }
  const at = withAnchor(state, id, exitAnchor);
  return applySlide(at, id, SIDE_EXIT_DIRECTION[gate.side], exitDistance(state.board));
}
